export function getScrollTop () {
  // cross-browser scroll top
  return document.documentElement.scrollTop || document.body.scrollTop || 0;
}

export function getSection (sectionName) {
  return window.jmAppState.sections.find((section) => section.name === sectionName);
}

export function invalidateLayoutMeasurements () {
  window.jmAppState.layoutDirty = true;
}

export function readScrollState () {
  // Read and cache the scroll position
  window.jmAppState.trueScrollTop = window.jmAppState.scrollTop = getScrollTop();
  // If scrolling out of bounds (e.g. on iOS Safari) limit to bounds of page
  if (window.jmAppState.pageHeight) {
    const scrollLimit = window.jmAppState.pageHeight - window.jmAppState.windowHeight;
    if (window.jmAppState.scrollTop > scrollLimit) {
      window.jmAppState.scrollTop = scrollLimit;
    } else if (window.jmAppState.scrollTop < 0) {
      window.jmAppState.scrollTop = 0;
    }
  }
}

export function readViewportState () {
  const viewport = window.visualViewport;
  window.jmAppState.windowHeight = Math.round(viewport ? viewport.height : window.innerHeight);
  window.jmAppState.windowWidth = Math.round(viewport ? viewport.width : window.innerWidth);
}

export function readLayoutState () {
  // Initialise section state
  if (!window.jmAppState.sections.length) {
    window.jmAppState.sections = Object.values(document.querySelectorAll(".sectionFrame")).map(
      section => {
        return {
          name: section.dataset.sectionName,
          element: section,
          screenStatus: '',
        };
      }
    );
  }
  // Do DOM reads to establish and cache section layout data
  readViewportState();
  window.jmAppState.pageHeight =  Math.floor(document.documentElement.getBoundingClientRect().height);
  for (let section of window.jmAppState.sections) {
    const rect = section.element.getBoundingClientRect();
    section.offsetTop = rect.top + window.jmAppState.trueScrollTop; // Distance from top of page to top of this section
    section.height = rect.height;
    section.width = rect.width;
    section.offsetBottom = section.offsetTop + section.height; // Distance from top of page to bottom of this section
  }
}

export function readSectionState () {
  // Calculate offsets etc. from cached data
  const {windowHeight, scrollTop} = {...window.jmAppState};
  const windowTopOffset = scrollTop;
  const windowCenterOffsetY = Math.round(windowHeight / 2) + scrollTop;

  for (let section of window.jmAppState.sections) {
    const {offsetTop, offsetBottom, height} = {...section};

    // Adjust height to ignore bottom borders due to overlap
    const frameHeight = Math.floor(Math.min(height, windowHeight) - 15);
    const frameBottomOffset = scrollTop + frameHeight;
    const adjustedOffsetBottom = Math.floor(offsetBottom - 15);

    // calculate how much of the element is onscreen, or how much of the screen is filled by the element
    const spaceAbove = Math.floor(Math.min(Math.max(offsetTop - windowTopOffset, 0), frameHeight));
    const spaceBelow = Math.floor(Math.min(Math.max(frameBottomOffset - adjustedOffsetBottom, 0), frameHeight));
    const onScreenHeight = Math.floor(frameHeight - (spaceAbove + spaceBelow));
    const onScreenPortion = onScreenHeight / frameHeight;
    const onCenter = onScreenPortion > 0 ? offsetTop < windowCenterOffsetY && Math.floor(offsetBottom - 8) > windowCenterOffsetY : false;

    // Update
    section.onScreenPortion = onScreenPortion;
    section.onCenter = onCenter;
  }
}

export function updateCachedMeasurements () {
  readScrollState();
  readViewportState();
  if (window.jmAppState.layoutDirty) {
    readLayoutState();
    window.jmAppState.layoutDirty = false;
  }
  readSectionState();
}

export function writeActiveSection () {
  // Do DOM writes to update the layout, based on cached positions

  // Get the section on the center line
  const oldActiveSectionName = window.jmAppState.activeSection ? window.jmAppState.activeSection.name : '';
  window.jmAppState.activeSection = window.jmAppState.sections.find((section) => section.onCenter);
  if (window.jmAppState.activeSection.name !== oldActiveSectionName) {
    // Update active section on body
    document.body.dataset.activeSection = window.jmAppState.activeSection.name;

    // Reveal active section (this class stays on after being added)
    window.jmAppState.activeSection.element.classList.add('reveal');
  }

  // Update onscreen status
  for (let [index, section] of window.jmAppState.sections.entries()) {
    if (section.onScreenPortion === 1) {
      if (section.screenStatus !== 'fullyOnScreen') {
        section.element.classList.add('fullyOnScreen');
        section.element.classList.remove('offScreen');
        section.screenStatus = 'fullyOnScreen';
      }
    } else if (section.onScreenPortion === 0) {
      if (section.screenStatus !== 'offScreen') {
        section.element.classList.add('offScreen');
        section.element.classList.remove('fullyOnScreen');
        section.screenStatus = 'offScreen';
      }
    } else if (section.screenStatus !== 'onscreen') {
      section.element.classList.remove('fullyOnScreen');
      section.element.classList.remove('offScreen');
      section.screenStatus = 'onscreen';
    }

    if (section.onScreenPortion >= .85) {
      // Reveal detail animations when 85% of the screen is filled by this section
      if (section.detailAnimationsOn !== true) {
        section.element.classList.add('detailAnimationsOn');
        section.detailAnimationsOn = true;
      }
    } else if (section.onScreenPortion === 0) {
      // But don't hide them until the section is completely hidden again
      if (section.detailAnimationsOn !== false) {
        section.element.classList.remove('detailAnimationsOn');
        section.detailAnimationsOn = false;
      }
    }

    // Special case - reset astronaut animation
    if (section.onScreenPortion === 0 && section.spaceManOrbitTimeout) {
      window.clearTimeout(section.spaceManOrbitTimeout);
      section.spaceManOrbitTimeout = undefined;
      section.element.querySelector('.astronaut').classList.remove('orbit');
    }
  }
};

export function scrollToClosestSection () {
  // Ignore scrolling events triggered by this function (prevent scroll loop)
  if (window.jmAppState.scrollingSection) return;
  // Smoothly position frame in window, if it is not totally on screen and is close to being aligned
  const threshold = 0.6;
  if (window.jmAppState.activeSection.onScreenPortion >= threshold && window.jmAppState.activeSection.onScreenPortion < 1) {
    scrollToSection(window.jmAppState.activeSection.name);
  }
};
export function scrollToSection (sectionName) {
  // Prevent a scroll loop
  window.jmAppState.scrollingSection = true;
  window.setTimeout(function () {
    window.jmAppState.scrollingSection = false;
  }, 750);
  // Smoothly scroll to a section
  const section = getSection(sectionName);
  section.element.scrollIntoView({
    behavior: "smooth",
    block: "nearest"
  });
};

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
function debounce(func, wait, immediate) {
	var timeout;
	return function() {
		var context = this, args = arguments;
		var later = function() {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
};

export function initSections() {
  // Inital measurement values
  updateCachedMeasurements();

  window.addEventListener('resize', invalidateLayoutMeasurements);
  window.addEventListener('load', invalidateLayoutMeasurements, { once: true });

  // Listen for scroll. Increased delay from 250 to 500 to ensure it fires after the smooth scroll polyfill has stopped ignoring scroll instructions (the polyfill is basically doing their own debouncing)
  window.addEventListener("scroll", debounce(scrollToClosestSection, 500), { passive: true });
}